// frontend/packages/business-console/src/pages/Scheduling/ApprovalsTab.jsx
//
// The request-to-book queue: bookings in `pending_confirmation` awaiting the
// tenant's approve/decline decision (doc 20 sec 4.5 #2). Approve calls the
// confirm transition, Decline calls the decline transition; both are audited
// server-side (D6). Reports back to the tab shell via onChanged so the badge
// count refreshes.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';

import {
  listPendingApprovals,
  confirmBooking,
  declineBooking,
} from '../../services/schedulingService';
import { Surface, useTokens } from '../../components/primitives';
import { formatDateTime, bookingCustomerLabel } from './helpers';

function serverMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

export default function ApprovalsTab({ onChanged }) {
  const t = useTokens();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listPendingApprovals({ limit: 100 });
      setItems(result?.docs || []);
    } catch (err) {
      setError(serverMessage(err, "Couldn't load pending requests."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (booking, kind) => {
    if (busyId) return;
    setBusyId(booking._id);
    setActionError(null);
    try {
      if (kind === 'approve') await confirmBooking(booking._id);
      else await declineBooking(booking._id);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setActionError(
        serverMessage(
          err,
          kind === 'approve'
            ? "Couldn't approve this request."
            : "Couldn't decline this request."
        )
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
      {error ? (
        <Alert severity="error" onClose={() => load()}>
          {error}
        </Alert>
      ) : null}
      {actionError ? (
        <Alert severity="error" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Surface
          sx={{
            borderStyle: 'dashed',
            py: 6,
            px: 3,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <TaskAltRoundedIcon
            sx={{ fontSize: 44, color: 'text.secondary', opacity: 0.6 }}
          />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            No requests waiting
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', maxWidth: 380 }}
          >
            When a customer asks to book a request-to-book service, it appears
            here for you to approve or decline.
          </Typography>
        </Surface>
      ) : (
        <Stack spacing={1.5}>
          {items.map((b) => (
            <Surface key={b._id} sx={{ p: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 1.5,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700 }}>
                    {bookingCustomerLabel(b)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: t.muted }}>
                    {b.appointmentTypeName || b.serviceName || 'Appointment'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}
                  >
                    {formatDateTime(b.startUtc)}
                  </Typography>
                </Box>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ flexShrink: 0, justifyContent: 'flex-end' }}
                >
                  <Button
                    size="small"
                    color="inherit"
                    variant="outlined"
                    disabled={busyId === b._id}
                    onClick={() => act(b, 'decline')}
                  >
                    Decline
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={
                      busyId === b._id ? (
                        <CircularProgress size={14} />
                      ) : (
                        <CheckCircleOutlineRoundedIcon />
                      )
                    }
                    disabled={busyId === b._id}
                    onClick={() => act(b, 'approve')}
                  >
                    Approve
                  </Button>
                </Stack>
              </Box>
            </Surface>
          ))}
        </Stack>
      )}
    </Box>
  );
}
